/**
 * Landlord repair-timeline copy for activity markers (RA-TIMELINE-LABELS) and the readable
 * detail of the photo-attachment events (US-63): a count plus one plain-language reason per
 * photo that was not added, so the raw payload codes never need to be read.
 */

/** Friendly marker labels. Any event type not listed falls back to a humanized event code. */
export const ACTIVITY_EVENT_LABELS: Record<string, string> = {
  'case.escalation_raised': 'Agent escalated a question to you',
  'case.escalation_answered': 'You answered the agent',
  'attachment.added': 'Tenant added photos',
  'attachment.removed': 'Tenant removed a photo',
  'attachment.skipped': "Some emailed photos couldn't be added",
};

/** One plain sentence per reason an emailed photo was not added (mirrors the tenant notice). */
const SKIP_REASON_COPY: Record<string, string> = {
  unsupported_type: 'File type not supported (JPEG, PNG or WebP only).',
  too_large: 'Photo is larger than the 10 MB limit.',
  too_many: 'The request already has the maximum number of photos.',
  case_closed: 'The request is closed, so photos can no longer be added.',
  feature_disabled: 'Photos can no longer be added to this request.',
  storage_error: "The photo couldn't be saved.",
};
const UNKNOWN_SKIP_REASON = "The photo couldn't be added.";

const plural = (n: number) => `${n} photo${n === 1 ? '' : 's'}`;

/**
 * Readable lines for an attachment.added / attachment.skipped payload; empty for every other
 * event or a payload without the expected shape (the caller then keeps the raw expander).
 */
export function attachmentActivityLines(eventType: string, payload: Record<string, unknown> | null | undefined): string[] {
  if (!payload) return [];
  if (eventType === 'attachment.added' && typeof payload.count === 'number') {
    const names = Array.isArray(payload.filenames) ? payload.filenames.filter((f): f is string => typeof f === 'string') : [];
    const via = payload.source === 'email' ? ' by email' : '';
    return [`${plural(payload.count)} added${via}${names.length > 0 ? `: ${names.join(', ')}` : ''}`];
  }
  if (eventType === 'attachment.skipped' && Array.isArray(payload.skipped)) {
    const skipped = payload.skipped.filter(
      (s): s is { filename?: unknown; reason?: unknown } => typeof s === 'object' && s !== null,
    );
    return [
      `${plural(skipped.length)} not added`,
      ...skipped.map((s) => {
        const name = typeof s.filename === 'string' && s.filename ? s.filename : 'photo';
        const reason = typeof s.reason === 'string' ? (SKIP_REASON_COPY[s.reason] ?? UNKNOWN_SKIP_REASON) : UNKNOWN_SKIP_REASON;
        return `${name}: ${reason}`;
      }),
    ];
  }
  return [];
}
